"use client";

import React, { useEffect, useState } from "react";
import { ArrowUpRight, BookOpen } from "lucide-react";
import type { AgentSourceLink } from "@/types/createAgent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface BlogSourceMeta {
  title: string;
  description?: string;
  image?: string;
}

/**
 * Slug → metadata map built from the public blog feed. Cached for the page
 * lifetime — the feed is static and every sources modal shares it.
 */
let feedMetaPromise: Promise<Map<string, BlogSourceMeta>> | null = null;

function loadBlogSourceMeta(): Promise<Map<string, BlogSourceMeta>> {
  if (!feedMetaPromise) {
    feedMetaPromise = fetch("/blog/feed.json")
      .then(response => (response.ok ? response.json() : null))
      .then(feed => {
        const map = new Map<string, BlogSourceMeta>();
        for (const item of feed?.items ?? []) {
          const slug = String(item.url ?? item.id ?? "")
            .split("/")
            .filter(Boolean)
            .pop();
          if (!slug) continue;
          map.set(slug, {
            title: item.title,
            description: item.content_text,
            image: item.image,
          });
        }
        return map;
      })
      .catch(() => {
        // Allow a retry on the next open instead of caching the failure.
        feedMetaPromise = null;
        return new Map<string, BlogSourceMeta>();
      });
  }
  return feedMetaPromise;
}

function SourceCard({
  source,
  meta,
}: {
  source: AgentSourceLink;
  meta?: BlogSourceMeta;
}) {
  // Prefer the image carried on the source itself so the thumbnail renders
  // immediately, without depending on the blog-feed fetch resolving. The feed
  // lookup is a fallback for sources persisted before the field existed.
  const image = source.image ?? meta?.image;
  return (
    <a
      href={`/blog/${source.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      data-agent-source-card
      className="group/source flex min-w-0 touch-manipulation items-stretch gap-3 rounded-xl border border-border bg-background p-2.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-logo/35 hover:bg-muted/30 hover:shadow-md motion-reduce:hover:translate-y-0"
    >
      <span className="relative block h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-muted">
        {image ? (
          // eslint-disable-next-line @next/next/no-img-element -- remote proxied blog images; next/image domain config not needed for this thumbnail
          <img
            src={image}
            alt=""
            width={112}
            height={80}
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover/source:scale-105 motion-reduce:group-hover/source:scale-100"
          />
        ) : (
          <span className="flex size-full items-center justify-center text-muted-foreground/60">
            <BookOpen className="size-6" aria-hidden="true" />
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <span className="flex min-w-0 items-start justify-between gap-2">
          <span className="line-clamp-2 min-w-0 text-sm font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
            {meta?.title ?? source.title}
          </span>
          <ArrowUpRight
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover/source:translate-x-0.5 group-hover/source:-translate-y-0.5 motion-reduce:group-hover/source:translate-x-0 motion-reduce:group-hover/source:translate-y-0"
            aria-hidden="true"
          />
        </span>
        {meta?.description && (
          <span className="line-clamp-2 min-w-0 text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
            {meta.description}
          </span>
        )}
      </span>
    </a>
  );
}

/**
 * A single compact "Sources" chip under the reply; clicking opens a modal
 * with rich guide cards (hero image, title, description) linking out to the
 * blog posts the agent drew on.
 */
export default function MessageSources({
  sources,
  inline = false,
}: {
  sources: AgentSourceLink[];
  /**
   * When true, render as an inline-flex chip (a `<span>`) so it can trail the
   * last word of the reply. Defaults to the standalone block layout.
   */
  inline?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [metaBySlug, setMetaBySlug] = useState<Map<
    string,
    BlogSourceMeta
  > | null>(null);

  useEffect(() => {
    if (!open || metaBySlug !== null) return;
    let cancelled = false;
    loadBlogSourceMeta().then(map => {
      if (!cancelled) setMetaBySlug(map);
    });
    return () => {
      cancelled = true;
    };
  }, [open, metaBySlug]);

  if (sources.length === 0) {
    return null;
  }

  const chip = (
    <>
      <button
        type="button"
        data-agent-sources-chip
        onClick={() => setOpen(true)}
        className="inline-flex max-w-full touch-manipulation items-center gap-1.5 rounded-full border border-border/70 bg-muted/25 px-2.5 py-1 text-xs font-medium text-foreground/75 align-middle transition-colors hover:border-brand-logo/40 hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <BookOpen className="size-3.5 shrink-0" aria-hidden="true" />
        <span>{sources.length === 1 ? "Source" : "Sources"}</span>
        <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
          {sources.length}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>From our guides</DialogTitle>
            <DialogDescription>
              The advice in this reply draws on these Build-a-Dress guides.
            </DialogDescription>
          </DialogHeader>
          <div className="grid min-w-0 gap-2.5">
            {sources.map(source => (
              <SourceCard
                key={source.slug}
                source={source}
                meta={metaBySlug?.get(source.slug)}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  if (inline) {
    return (
      <span
        data-agent-source-links
        className="ml-1.5 inline-flex max-w-full align-middle"
      >
        {chip}
      </span>
    );
  }

  return (
    <div data-agent-source-links className="mt-3">
      {chip}
    </div>
  );
}
